import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const STORAGE_KEY = "munja_preview_role";

const PermissionsContext = createContext({
  matrix: null, modules: [], actions: [], roles: [],
  can: () => true, role: "", reload: () => {},
  previewRole: null, setPreviewRole: () => {}, clearPreview: () => {}, isPreviewing: false,
  ready: false,
});

export function PermissionsProvider({ children }) {
  const { user } = useAuth();
  const [data, setData] = useState({ matrix: null, modules: [], actions: [], roles: [] });
  const [ready, setReady] = useState(false);
  const [previewRole, setPreviewRoleState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });

  const reload = useCallback(async () => {
    if (!user || user === false) return;
    try {
      const { data } = await api.get("/settings/role-permissions");
      setData(data);
      setReady(true);
    } catch {
      // On failure we still mark as ready (with empty matrix) so the sidebar
      // can render the conservative "non-admin sees nothing extra" state
      // instead of hanging forever at the loading skeleton.
      setReady(true);
    }
  }, [user]);

  useEffect(() => { reload(); }, [reload]);

  // Auto-refetch every 30s so admin-side role-permission changes propagate
  // to currently-logged-in users without forcing a logout.
  useEffect(() => {
    if (!user || user === false) return;
    const id = setInterval(reload, 30000);
    return () => clearInterval(id);
  }, [user, reload]);

  // refetch on permission change event
  useEffect(() => {
    function onUpd() { reload(); }
    window.addEventListener("permissions-updated", onUpd);
    return () => window.removeEventListener("permissions-updated", onUpd);
  }, [reload]);

  // Auto-clear preview when user logs out or changes
  useEffect(() => {
    if (!user || user === false) {
      try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
      setPreviewRoleState(null);
    }
  }, [user]);

  const realRole = (user && user.role) || "";
  const isAdmin = realRole === "admin" || (user && user.is_super_admin);
  // Roles allowed to create/edit roles & permissions (mirrors backend
  // `require_role("admin", "geschaeftsfuehrung")` on /settings/roles* +
  // /settings/role-permissions*).
  const canManageRoles = isAdmin || realRole === "geschaeftsfuehrung";

  // Effective role: when admin previews another role, use that role for permission checks
  const effectiveRole = isAdmin && previewRole ? previewRole : realRole;
  const isPreviewing = isAdmin && !!previewRole;

  function setPreviewRole(roleKey) {
    if (!isAdmin) return;
    if (!roleKey || roleKey === realRole) { clearPreview(); return; }
    try { localStorage.setItem(STORAGE_KEY, roleKey); } catch { /* ignore */ }
    setPreviewRoleState(roleKey);
  }
  function clearPreview() {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    setPreviewRoleState(null);
  }

  const can = useCallback((module, action = "view") => {
    // Phase 5Z.19n — Strict pre-load default:
    //   Admins always allowed (no flash-of-restricted-UI for owners).
    //   Non-admins get FALSE until the matrix arrives → prevents the
    //   "flash of unauthorized content" where dispatchers briefly saw
    //   admin-only sidebar items between login and matrix-fetch.
    if (effectiveRole === "admin") return true;
    if (!data.matrix) return false;
    const r = data.matrix[effectiveRole];
    if (!r) return false;
    const m = r[module];
    if (!m) return false;
    return Boolean(m[action]);
  }, [data.matrix, effectiveRole]);

  return (
    <PermissionsContext.Provider value={{
      ...data, can, role: effectiveRole, realRole, reload, ready,
      previewRole, setPreviewRole, clearPreview, isPreviewing, isAdmin, canManageRoles,
    }}>
      {children}
    </PermissionsContext.Provider>
  );
}

export function usePermissions() { return useContext(PermissionsContext); }
